/**
 * 对话页的纯逻辑：把对话事件折成页面上一条条要画的东西，以及读 text/event-stream。
 * 事件形状来自 GET /api/voko/agents/:id/chat 和 …/chat/stream（docs-html/done/agent-chat.html）：
 * 存下的事件有连续的 seq；*_delta 片段 seq 为 null，只在实时流里出现，同一 item_id 的完整事件到了就由它取代。
 *
 * @typedef {{ seq: number|null, type: string, payload: any, created_at?: string }} ChatEvent
 * @typedef {(
 *   { kind: 'owner', key: string, text: string, at: string|null } |
 *   { kind: 'stop', key: string } |
 *   { kind: 'turn', key: string, source: 'owner'|'heartbeat', status: string|null } |
 *   { kind: 'agent', key: string, text: string, phase: string|null, streaming: boolean } |
 *   { kind: 'reasoning', key: string, text: string, streaming: boolean } |
 *   { kind: 'command', key: string, command: string, cwd: string|null, output: string, exit_code: number|null, truncated: boolean, duration_ms: number|null, running: boolean } |
 *   { kind: 'files', key: string, changes: Array<{ path: string, kind: string, diff: string, truncated: boolean }> }
 * )} ChatItem
 */

/**
 * 页面上的对话状态：存下的事件（按 seq）和实时片段（按 item_id）。
 * @returns {{ saved: Map<number, ChatEvent>, live: Map<string, { type: string, text: string }>, online: boolean, working: boolean }}
 */
export function emptyChat() {
  return { saved: new Map(), live: new Map(), online: false, working: false }
}

/** 片段事件 → 它累积成的那类内容。 */
const DELTA_OF = { agent_delta: 'agent', reasoning_delta: 'reasoning', command_output_delta: 'command' }

/**
 * 收下一条事件（历史里的或实时流里的），原地更新状态。
 * @param {ReturnType<typeof emptyChat>} chat
 * @param {ChatEvent} event
 */
export function applyEvent(chat, event) {
  switch (event.type) {
    case 'presence':
      chat.online = event.payload.online
      return
    case 'agent_delta':
    case 'reasoning_delta':
    case 'command_output_delta': {
      const prev = chat.live.get(event.payload.item_id)
      chat.live.set(event.payload.item_id, { type: DELTA_OF[event.type], text: (prev === undefined ? '' : prev.text) + event.payload.delta })
      return
    }
    case 'owner_message':
    case 'stop':
    case 'turn_started':
    case 'turn_completed':
    case 'agent_message':
    case 'reasoning':
    case 'command_started':
    case 'command_completed':
    case 'file_change':
      if (event.type === 'turn_started') chat.working = true
      if (event.type === 'turn_completed') chat.working = false
      chat.saved.set(event.seq, event)
      // 完整的消息到了：同一 item_id 的打字机片段不再需要。命令开始时片段还要继续用来显示输出。
      if (event.type !== 'command_started' && event.payload.item_id !== undefined) chat.live.delete(event.payload.item_id)
      return
    default:
      // 平台加了事件类型要同时改这里；不认识就是漏改了。
      throw new Error(`Unhandled status: ${event.type}, id: ${event.seq}`)
  }
}

/**
 * 按顺序折成要画的条目：存下的事件按 seq，最后接上还在打字的片段。
 * @param {ReturnType<typeof emptyChat>} chat
 * @returns {ChatItem[]}
 */
export function chatItems(chat) {
  /** @type {ChatItem[]} */
  const items = []
  /** @type {Map<string, ChatItem>} 有 item_id 的条目，后来的事件按它更新 */
  const byItem = new Map()
  const seqs = [...chat.saved.keys()].sort((a, b) => a - b)
  for (const seq of seqs) {
    const { type, payload, created_at } = /** @type {ChatEvent} */ (chat.saved.get(seq))
    switch (type) {
      case 'owner_message':
        items.push({ kind: 'owner', key: `s${seq}`, text: payload.text, at: created_at ?? null })
        break
      case 'stop':
        items.push({ kind: 'stop', key: `s${seq}` })
        break
      case 'turn_started':
        items.push({ kind: 'turn', key: `s${seq}`, source: payload.source, status: null })
        byItem.set(`turn:${payload.turn_id}`, items.at(-1))
        break
      case 'turn_completed': {
        const turn = byItem.get(`turn:${payload.turn_id}`)
        if (turn !== undefined && turn.kind === 'turn') turn.status = payload.status
        break
      }
      case 'agent_message':
        items.push({ kind: 'agent', key: payload.item_id, text: payload.text, phase: payload.phase, streaming: false })
        break
      case 'reasoning':
        items.push({ kind: 'reasoning', key: payload.item_id, text: payload.text, streaming: false })
        break
      case 'command_started': {
        const live = chat.live.get(payload.item_id)
        const item = { kind: 'command', key: payload.item_id, command: payload.command, cwd: payload.cwd, output: live === undefined ? '' : live.text, exit_code: null, truncated: false, duration_ms: null, running: true }
        items.push(/** @type {ChatItem} */ (item))
        byItem.set(payload.item_id, items.at(-1))
        break
      }
      case 'command_completed': {
        const done = { command: payload.command, output: payload.output, exit_code: payload.exit_code, truncated: payload.truncated, duration_ms: payload.duration_ms, running: false }
        const started = byItem.get(payload.item_id)
        if (started !== undefined && started.kind === 'command') Object.assign(started, done)
        else items.push({ kind: 'command', key: payload.item_id, cwd: null, ...done })
        break
      }
      case 'file_change':
        items.push({ kind: 'files', key: payload.item_id, changes: payload.changes })
        break
      default:
        throw new Error(`Unhandled status: ${type}, id: ${seq}`)
    }
  }
  // 还在打字的回复和思考：还没有完整事件，排在最后。
  for (const [item_id, live] of chat.live) {
    if (live.type === 'agent') items.push({ kind: 'agent', key: item_id, text: live.text, phase: null, streaming: true })
    if (live.type === 'reasoning') items.push({ kind: 'reasoning', key: item_id, text: live.text, streaming: true })
  }
  return items
}

/**
 * 从 text/event-stream 的缓冲里取出完整的帧，返回解析出的事件和剩下没结束的部分。
 * @param {string} buffer
 * @returns {{ events: ChatEvent[], rest: string }}
 */
export function parseFrames(buffer) {
  const events = []
  let rest = buffer
  let at
  while ((at = rest.indexOf('\n\n')) !== -1) {
    const frame = rest.slice(0, at)
    rest = rest.slice(at + 2)
    for (const line of frame.split('\n')) if (line.startsWith('data: ')) events.push(JSON.parse(line.slice(6)))
  }
  return { events, rest }
}
